import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, Filter, CheckCircle, XCircle, Clock, Eye } from 'lucide-react';
import EnhancedTable from '../../components/common/EnhancedTable';
import ExpandableText from '../../components/common/ExpandableText';
import { getLeaseApplications } from '../../services/supabase/lease-applications';
import { formatDate } from '../../utils/dateUtils';
import type { LeaseApplication } from '../../types/lease-application';
import { useNavigationRefresh } from '../../hooks/useNavigationRefresh';
import LeaseApplicationDetailsModal from '../../components/LeaseApplications/LeaseApplicationDetailsModal';

export default function ReadOnlyApplications() {
  const [searchQuery, setSearchQuery] = useState('');
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState<LeaseApplication | null>(null);

  // Use the custom hook to handle navigation refreshes
  useNavigationRefresh(['applications']);

  const { data: applications, isLoading } = useQuery({
    queryKey: ['applications'],
    queryFn: getLeaseApplications
  });

  const filteredApplications = applications?.filter(app => {
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      return (
        app.app_id.toString().includes(query) ||
        app.app_lot.toLowerCase().includes(query) ||
        app.app_desc?.toLowerCase().includes(query)
      );
    }
    return true;
  });

  const getStatusIcon = (app: LeaseApplication) => {
    if (app.app_final === 'Y') return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (app.app_approv_date) return <CheckCircle className="h-5 w-5 text-blue-500" />;
    return <Clock className="h-5 w-5 text-yellow-500" />;
  };

  const getStatusText = (app: LeaseApplication) => {
    if (app.app_final === 'Y') return 'Completed';
    if (app.app_approv_date) return 'Approved';
    return 'Pending';
  };

  return (
    <div className="space-y-6 p-4 h-[calc(100vh-6rem)] overflow-y-auto">
      {/* Level 1: Page Header - Always visible */}
      <div className="pt-2 pb-4 border-b border-gray-200">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Lease Applications</h1>
          <p className="mt-1 text-sm text-gray-500">
            View and monitor lease applications
          </p>
        </div>
      </div>

      {/* Level 2: Controls Area */}
      <div className="py-4 border-b border-gray-200">
        <div className="flex gap-4">
          <div className="relative flex-1">
            <input
              type="text"
              placeholder="Search applications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
            />
            <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
          <button
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
          >
            <Filter className="h-5 w-5" />
          </button>
        </div>
        
        {isFilterOpen && (
          <div className="mt-4 p-4 bg-white border rounded-md shadow-sm">
            <h3 className="text-sm font-medium mb-3">Filters</h3>
            {/* Add filter controls here when needed */}
          </div>
        )}
      </div>

      {/* Enhanced table with fixed header and horizontal scrolling */}
      <div className="bg-white shadow-sm rounded-lg overflow-hidden mt-4">
        <EnhancedTable
          columns={[
            { 
              id: 'view', 
              header: 'View', 
              accessor: (row) => (
                <button
                  onClick={() => setSelectedApplication(row)}
                  className="w-10 h-10 flex items-center justify-center text-gray-600 hover:text-primary bg-gray-50 hover:bg-gray-100 rounded-lg"
                  title="View Details"
                >
                  <Eye className="h-6 w-6" />
                </button>
              ),
              width: 'narrow'
            },
            { 
              id: 'app_id', 
              header: 'Application ID', 
              accessor: (row) => row.app_id,
              width: 'narrow'
            },
            { 
              id: 'app_lot', 
              header: 'Lot', 
              accessor: (row) => row.app_lot,
              width: 'narrow'
            },
            { 
              id: 'app_type', 
              header: 'Type', 
              accessor: (row) => row.app_type,
              width: 'narrow'
            },
            { 
              id: 'app_sublease', 
              header: 'Sublease', 
              accessor: (row) => row.app_sublease || '-',
              width: 'narrow'
            },
            { 
              id: 'app_desc', 
              header: 'Description', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.app_desc || '-'} 
                  maxLength={50} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            },
            { 
              id: 'app_permit', 
              header: 'Permitted Use', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.app_permit || '-'} 
                  maxLength={50} 
                  className="text-sm"
                />
              ),
              width: 'wide'
            },
            { 
              id: 'status', 
              header: 'Status', 
              accessor: (row) => (
                <div className="flex items-center">
                  {getStatusIcon(row)}
                  <span className="ml-2 text-sm text-gray-900">
                    {getStatusText(row)}
                  </span>
                </div>
              ),
              width: 'medium'
            },
            { 
              id: 'app_date', 
              header: 'Submitted', 
              accessor: (row) => formatDate(row.app_date),
              width: 'narrow'
            },
            { 
              id: 'app_approv_date', 
              header: 'Approval Date', 
              accessor: (row) => row.app_approv_date ? formatDate(row.app_approv_date) : '-',
              width: 'narrow'
            },
            { 
              id: 'app_const_appr_date', 
              header: 'Construction Approval', 
              accessor: (row) => row.app_const_appr_date ? formatDate(row.app_const_appr_date) : '-',
              width: 'narrow'
            },
            { 
              id: 'app_dev_comp', 
              header: 'Development Completion', 
              accessor: (row) => row.app_dev_comp ? formatDate(row.app_dev_comp) : '-',
              width: 'narrow'
            },
            { 
              id: 'app_doc', 
              header: 'Application Document', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.app_doc || '-'} 
                  maxLength={30} 
                  className="text-sm text-gray-500"
                />
              ),
              width: 'wide'
            },
            { 
              id: 'app_appr_doc', 
              header: 'Approval Document', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.app_appr_doc || '-'} 
                  maxLength={30} 
                  className="text-sm text-gray-500"
                />
              ),
              width: 'wide'
            },
            { 
              id: 'app_supp_docs', 
              header: 'Supporting Documents', 
              accessor: (row) => (
                <ExpandableText 
                  text={row.app_supp_docs || '-'} 
                  maxLength={30} 
                  className="text-sm text-gray-500"
                />
              ),
              width: 'wide'
            }
          ]}
          data={filteredApplications || []}
          keyField="app_id"
          width="1800px"
          maxHeight="calc(100vh - 16rem)"
          loading={isLoading}
          emptyMessage="No applications found"
          horizontalScroll={true}
        />
      </div>

      {/* Details Modal */}
      {selectedApplication && (
        <LeaseApplicationDetailsModal
          application={selectedApplication}
          onClose={() => setSelectedApplication(null)}
        />
      )}
    </div>
  );
}
